/**
 * Modal para gestionar la credencial del portal del socio.
 *
 * Muestra estado actual y permite activar/regenerar/revocar. La clave
 * generada se muestra UNA vez en pantalla con botón de copiar — el server
 * solo guarda el hash, no se puede recuperar después.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Power, RotateCw } from "lucide-react";

import { ApiError, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  ConfirmDialog,
} from "../../components/ui";
import {
  activarCredencialSocio,
  obtenerCredencialSocio,
  revocarCredencialSocio,
  type CredencialActivada,
  type SocioRead,
} from "./api";

export function PortalSocioModal({
  socio,
  onClose,
}: {
  socio: SocioRead;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [recienGenerada, setRecienGenerada] =
    useState<CredencialActivada | null>(null);
  const [revoking, setRevoking] = useState(false);

  const credQ = useQuery({
    queryKey: ["socio-credencial", socio.id],
    queryFn: () => obtenerCredencialSocio(socio.id),
  });

  const activarMut = useMutation({
    mutationFn: () => activarCredencialSocio(socio.id),
    onSuccess: (cred) => {
      setRecienGenerada(cred);
      void qc.invalidateQueries({ queryKey: ["socio-credencial", socio.id] });
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "No se pudo activar la credencial",
      );
    },
  });

  const revocarMut = useMutation({
    mutationFn: () => revocarCredencialSocio(socio.id),
    onSuccess: () => {
      toast.success("Acceso al portal revocado");
      setRecienGenerada(null);
      setRevoking(false);
      void qc.invalidateQueries({ queryKey: ["socio-credencial", socio.id] });
    },
    onError: (err) => {
      setRevoking(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudo revocar");
    },
  });

  const cred = credQ.data;
  const tieneCredencial = cred !== null && cred !== undefined;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Acceso al portal del socio"
        subtitle={`${socio.nombre} · RUT ${socio.rut}`}
        icon={KeyRound}
        size="md"
        footer={
          <>
            {tieneCredencial && cred.activo && (
              <Button
                variant="secondary"
                size="sm"
                icon={Power}
                onClick={() => setRevoking(true)}
                disabled={revocarMut.isPending}
              >
                Revocar acceso
              </Button>
            )}
            <Button
              size="sm"
              icon={RotateCw}
              className="ml-auto"
              onClick={() => activarMut.mutate()}
              disabled={activarMut.isPending}
            >
              {tieneCredencial ? "Regenerar clave" : "Activar portal"}
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          {credQ.isPending && <Cargando inline texto="Cargando estado…" />}

          {recienGenerada && (
            <ClaveGeneradaCard
              cred={recienGenerada}
              onCopy={() =>
                navigator.clipboard
                  .writeText(recienGenerada.password)
                  .then(() => toast.success("Clave copiada"))
                  .catch(() => toast.error("No se pudo copiar"))
              }
            />
          )}

          {!recienGenerada && credQ.isSuccess && (
            <div
              className={
                tieneCredencial && cred.activo
                  ? "rounded-md border border-emerald-200 bg-emerald-50 p-3"
                  : "rounded-md border border-neutral-200 bg-neutral-50 p-3"
              }
            >
              {!tieneCredencial && (
                <p className="text-neutral-700">
                  Este socio aún <strong>no tiene acceso al portal</strong>.
                </p>
              )}
              {tieneCredencial && cred.activo && (
                <div className="space-y-1 text-emerald-900">
                  <p>
                    <strong>Acceso activo</strong> al portal.
                  </p>
                  <p className="text-xs">
                    Clave creada: {formatDateTime(cred.creada_at)}
                    <br />
                    Último ingreso:{" "}
                    {cred.last_login_at
                      ? formatDateTime(cred.last_login_at)
                      : "nunca"}
                  </p>
                </div>
              )}
              {tieneCredencial && !cred.activo && (
                <div className="space-y-1 text-amber-900">
                  <p>
                    <strong>Acceso revocado.</strong> Activa una nueva clave
                    para que el socio pueda volver a entrar.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <strong>Cómo funciona:</strong> el sistema genera una clave
            aleatoria que se muestra <em>una sola vez</em> en pantalla.
            Imprímela o anótala y entrégala al socio — después solo se puede
            regenerar.
          </div>
        </div>
      </Modal>

      {revoking && (
        <ConfirmDialog
          open
          title="Revocar acceso al portal"
          description={`${socio.nombre} no podrá entrar al portal hasta que actives una nueva clave.`}
          confirmLabel="Revocar acceso"
          loading={revocarMut.isPending}
          onCancel={() => setRevoking(false)}
          onConfirm={() => revocarMut.mutate()}
        />
      )}
    </>
  );
}

function ClaveGeneradaCard({
  cred,
  onCopy,
}: {
  cred: CredencialActivada;
  onCopy: () => void;
}): JSX.Element {
  return (
    <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-4 space-y-2">
      <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">
        Nueva clave generada
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded border border-emerald-300 bg-white px-3 py-2 text-lg font-mono font-bold text-emerald-900 tracking-widest text-center">
          {cred.password}
        </code>
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copiar clave"
          className="rounded-md bg-emerald-600 p-2 text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
          title="Copiar"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-emerald-900">
        ⚠ Anótala o imprímela ahora — esta es la <strong>única vez</strong>{" "}
        que la verás. Si se pierde, genera una nueva.
      </p>
    </div>
  );
}
